import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { aiApi } from '../api/ai.api';
import { itineraryApi } from '../api/itinerary.api';
import { toast } from '../components/ui/Toast';
import { applyDaySchedule, applyNextDayUpdate } from '../utils/activityOrder';
import { reorderEntry, stopIds, type HistoryEntry } from '../utils/itineraryHistoryEntries';
import type { ItineraryHistory } from './useItineraryHistory';
import type { AiProposal } from '../components/map/AiRouteProposalModal';
import type { Activity, ItineraryDay } from '../types';

/**
 * "Reorder this day so it stops zig-zagging", and the review card that follows.
 *
 * Keyed by `dayId` rather than by the map's day index, so the itinerary can use
 * it too: the same button on the map and in the plan has to do the same thing,
 * and until this existed the optimiser lived inside the map's route state and
 * could not be reached from the tab where the day is actually built.
 *
 * Proposing does not change anything — the user accepts, and only then is the
 * day reordered.
 */

export interface DayProposal extends AiProposal {
  dayId: string;
}

export interface DayOptimizer {
  proposal: DayProposal | null;
  /** The day whose proposal is being fetched, if any. */
  optimizingDayId: string | null;
  optimize: (dayId: string, stops: Activity[], hint?: string) => Promise<void>;
  accept: () => Promise<void>;
  discard: () => void;
}

interface Options {
  tripId: string;
  /** Needed for the day's start hour: the schedule cascades from it (ADR-0010). */
  days: ItineraryDay[];
  setDays: React.Dispatch<React.SetStateAction<ItineraryDay[]>>;
  /** ADR-0015: an accepted optimisation can be undone. Pass its `setDays` as `setDays`. */
  history?: ItineraryHistory;
}

export function useDayOptimizer({ tripId, days, setDays, history }: Options): DayOptimizer {
  const { t } = useTranslation();
  const [proposal, setProposal] = useState<DayProposal | null>(null);
  const [optimizingDayId, setOptimizingDayId] = useState<string | null>(null);

  const optimize = useCallback(async (dayId: string, stops: Activity[], hint?: string) => {
    if (stops.length < 2) return;

    setOptimizingDayId(dayId);
    try {
      const baseMsg = t('map.aiOptimizePrompt');
      const trimmed = hint?.trim();
      const userMsg = trimmed ? `${baseMsg}\n\n${t('map.aiOptimizeHintPrefix')}: ${trimmed}` : baseMsg;
      const { reply, actions } = await aiApi.chat(
        tripId, [{ role: 'user', content: userMsg }], 'routes', { dayId },
      );
      const action = actions.find(a => a.type === 'route_optimize_proposed');
      if (!action || action.type !== 'route_optimize_proposed') {
        toast(reply || t('itinerary.toast.optimizeNoResult'), 'info' as never);
        return;
      }

      const { orderedIds, legModes: proposedModes, reasoning, benefits } = action.data;
      const reordered = orderedIds
        .map((id: string) => stops.find(a => a.id === id))
        .filter((a: Activity | undefined): a is Activity => !!a);
      // A partial order would silently drop stops on accept — discard it whole.
      if (reordered.length !== stops.length) return;

      const legCount = Math.max(0, reordered.length - 1);
      const modes = (proposedModes ?? []).slice(0, legCount);
      // Legs the AI didn't rate are left to the server's own choice.
      while (modes.length < legCount) modes.push('');
      setProposal({ dayId, reordered, legModes: modes, reasoning, benefits: benefits ?? [] });
    } catch {
      toast(t('itinerary.toast.optimizeFailed'), 'error');
    } finally {
      setOptimizingDayId(null);
    }
  }, [tripId, t]);

  const apply = useCallback(async (): Promise<HistoryEntry | null> => {
    if (!proposal) return null;
    const { dayId, reordered } = proposal;
    const day = days.find(d => d.id === dayId);
    const before = stopIds(day);
    const reTimed = applyDaySchedule(day?.startTime ?? null, reordered);
    setProposal(null);
    setDays(prev => prev.map(d => (d.id === dayId ? { ...d, activities: reTimed } : d)));

    try {
      const { nextDayUpdate, activities } = await itineraryApi.reorderActivities(
        tripId, dayId, reTimed.map(a => a.id),
      );
      setDays(prev => {
        let updated = prev.map(d => (d.id === dayId ? { ...d, activities } : d));
        if (nextDayUpdate) updated = applyNextDayUpdate(updated, nextDayUpdate);
        return updated;
      });
      const message = t('itinerary.toast.optimizeApplied');
      if (!history) { toast(message, 'success'); return null; }
      const after = activities.filter(a => !a.isOrigin).map(a => a.id);
      return reorderEntry({ tripId, setDays, getDays: history.getDays }, dayId, before, after, message);
    } catch {
      toast(t('itinerary.toast.reorderFailed'), 'error');
      return null;
    }
  }, [proposal, days, setDays, tripId, t, history]);

  const accept = useCallback(async () => {
    if (history) await history.run(apply);
    else await apply();
  }, [history, apply]);

  const discard = useCallback(() => setProposal(null), []);

  return { proposal, optimizingDayId, optimize, accept, discard };
}
